import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ShieldCheck } from 'lucide-react';
import { drawResolvedTs, fetchRoundByNumber } from '../archive/api';
import { RoundAnchorCards } from '../verify/RoundAnchorCards';
import { RoundDigitsTable } from '../verify/RoundDigitsTable';
import { blockDigitRows, drawDigits, roundCloseTimestamp } from '../verify/roundDigits';
import { useAuthStore } from '../auth/authStore';
import { useCountryStore } from '../../shared/country/countryStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { localDateTime, utcDateTime } from '../../shared/lib/datetime';
import { normalizeWindow, windowResult } from '../../shared/milhar/rulesWindow';
import type { MilharTierId } from '../../shared/milhar/tiers';
import { fetchMilharStatus } from './api';

/**
 * 「这一期到底怎么算出来的」。
 *
 * <p><b>覆盖层，不是路由</b> —— 与账单弹层同一个理由：关掉它就回到那张表，
 * 标签页、滚动位置、窗口选择全都还在。走路由要把期号塞进 appStore 那条贯穿
 * 十余个函数的导航元组，代价大，还得自己存回这三样。
 *
 * <p><b>不新建接口。</b> Milhar 与房间三玩法共用 `rounds` 表同一个 round_number，
 * `fetchRoundByNumber` 拿到的就是这一期真实的锚定区块与哈希。
 */
export function MilharVerifyOverlay({ round, tier, label, result, onClose }: {
  round: number;
  tier: MilharTierId;
  /** History 行的日内序号（如 `#008`）。玩家点的是这一行，落地页得认得出是它。 */
  label: string;
  /** History 行自己的、权威的开奖号 —— 从行里原样带过来，这里绝不重算，见下方注释。 */
  result: string;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const token = useAuthStore((state) => state.token);
  const countryCode = useCountryStore((state) => state.country.code);

  const roundQuery = useQuery({
    queryKey: ['milhar-verify-round', round],
    queryFn: () => fetchRoundByNumber(round),
    staleTime: 5 * 60_000,
  });
  // 窗口由后端下发，前端不许写死（rulesWindow.ts 文件头）。状态接口已被
  // 游戏页缓存，这里命中的多半是缓存，不会多打一次。
  const statusQuery = useQuery({
    // key 四段必须与游戏页（MilharPage.tsx）、账本页（MilharLedgerPage.tsx）
    // 完全一致才能命中同一份缓存 —— 否则这里多半不会真的发请求，而是各打
    // 各的。countryCode 不能省：状态按国家区分（窗口配置、开关都可能因国
    // 家而异），少了它会把不同国家的响应错误地当成同一份缓存命中，别为了
    // "简化" 把它删掉。
    // 注意 fetchMilharStatus 的签名是 (token, tier)，参数顺序反了会把 tier
    // 当成 token 发出去，config 拿不到，窗口静默退回 FALLBACK_WINDOW。
    queryKey: ['milhar-status', tier, token || 'guest', countryCode],
    queryFn: () => fetchMilharStatus(token, tier),
    staleTime: 30_000,
  });

  const data = roundQuery.data;
  const window = normalizeWindow(statusQuery.data?.config?.digitWindow, tier);
  // 这三个都走 roundDigits.ts 里迁出的函数，不要在这里重写：
  // draw_digits 可能是数组，直接 String() 会得到 "3,6,5,4,…"；
  // B 行取自 eth_digits，从哈希里现抓数字是第二份实现。
  const digits = data ? drawDigits(data) : '';
  const blockDigits = data ? blockDigitRows(data) : [];
  // derivedResult 只是「自己按步骤算一遍」的教学展示，**绝不是**这一期的权威
  // 结果 —— window 来自当前 /status 配置查询，把它套到一个历史期上会得到
  // 「用今天的规则重算过去」，跟 spec §9.3 明令禁止的 jackpotContribPct 写死
  // 是同一类错误：档位的取窗规则后台可调，旧期的窗口可能早就不是这个了。
  // 这一期真正展示给玩家的号码用 `result` prop，从 History 行原样带下来
  // （本次评审 Finding 4）。
  const derivedResult = digits.length >= window[1]
    ? windowResult(digits.split('').map(Number), window)
    : '';
  // 推导和权威结果对不上——窗口配置已经变过、或者 draw_digits 缺失/过短——都
  // 不能悄悄吞掉：玩家点进这页正是想「自己核对一遍」，一个被发现的矛盾远比
  // 一个被悄悄盖过去的矛盾更值得信任。
  const derivationMismatch = !!result && derivedResult !== result;
  // 「开奖时刻」= 第三个锚定块的时间戳，缺块时退回本期关闭时刻。
  // 与归档明细抬头同一个口径，两屏对得上。
  const drawnAt = data ? drawResolvedTs(data) : 0;

  return (
    <section className="ds-screen milhar-screen milhar-verify-screen">
      <header className="ds-header shrink-0">
        <button className="icon-button tap" type="button" onClick={onClose} aria-label={t('back')}>
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <h1 className="flex-1 text-[20px] leading-[26px] font-extrabold">
          {t('milharVerifyTitle')}
        </h1>
        <span className="icon-button" aria-hidden="true"><ShieldCheck size={18} /></span>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
        {roundQuery.isError ? (
          <div className="milhar-stats-fallback">
            <p>{t('milharVerifyUnavailable')}</p>
            <button type="button" className="milhar-verify-retry tap"
              onClick={() => roundQuery.refetch()}>{t('milharStatsRetry')}</button>
          </div>
        ) : !data ? (
          <div className="milhar-stats-skeleton">
            {Array.from({ length: 8 }, (_, i) => <span key={i} className="sk" />)}
          </div>
        ) : (
          <>
            <div className="milhar-verify-head">
              {/* #008 主号 + 小字全局 #40247（spec §4.2 第 1 段）。玩家是从
                  History 那一行的盾牌点进来的，那一行显示的就是 #008，落地页
                  只报全局期号会让人怀疑自己点错了行。 */}
              <div className="milhar-verify-round">
                <span className="milhar-verify-round-label mono">{label || `#${round}`}</span>
                <span className="milhar-verify-round-global">
                  {t('milharVerifyGlobalId')} #{round}
                </span>
              </div>
              <dl className="milhar-verify-times">
                {/* 时刻缺失就整条不渲染 —— 0 会被显示成 1970 */}
                {drawnAt > 0 ? (
                  <>
                    <div><dt>UTC</dt><dd>{utcDateTime(drawnAt)}</dd></div>
                    <div><dt>{t('localTimeTag')}</dt><dd>{localDateTime(drawnAt)}</dd></div>
                  </>
                ) : null}
              </dl>
            </div>

            {/* 权威结果：来自 History 行的 result prop，不是下面 derivedResult 现算的——
                理由见上面那段大注释。draw_digits 缺失/过短时 derivedResult 会是空串，
                但这一段照样要渲染，不能让「这一期到底开了什么」在验算页上消失。 */}
            {result ? (
              <section className="milhar-verify-sec">
                <h2>{t('milharVerifyResultTitle')}</h2>
                <div className="milhar-verify-draw mono" aria-label={result}>
                  {result.split('').map((c, i) => <span key={i}>{c}</span>)}
                </div>
                {derivationMismatch ? (
                  <p className="milhar-verify-mismatch">{t('milharVerifyMismatch')}</p>
                ) : null}
              </section>
            ) : null}

            <section className="milhar-verify-sec">
              <h2>{t('milharVerifyDigitsTitle')}</h2>
              <RoundDigitsTable blockDigits={blockDigits} digits={digits} highlight={window} />
            </section>

            <section className="milhar-verify-sec">
              <h2>{t('milharVerifyStepsTitle')}</h2>
              <ol className="milhar-verify-steps">
                <li>
                  <b>1</b>
                  <p>{t('milharVerifyStep1').replace('%d%', digits || '—')}</p>
                </li>
                <li>
                  <b>2</b>
                  <p>{t('milharVerifyStep2')
                    .replace('%a%', String(window[0]))
                    .replace('%b%', String(window[1]))
                    .replace('%r%', derivedResult || '—')}</p>
                </li>
              </ol>
            </section>

            <section className="milhar-verify-sec">
              <h2>{t('milharVerifyBlocksTitle')}</h2>
              <RoundAnchorCards anchors={data.anchors || []} roundCloseTs={roundCloseTimestamp(data)} />
            </section>

            <p className="milhar-verify-foot">{t('milharVerifyFoot')}</p>
          </>
        )}
      </div>
    </section>
  );
}
